/** Mirror — reflect marks in realtime. Symmetry as a serendipity engine. */
import type { AffectModule, ModuleContext } from '../../core/module-api';
import { cfgStr } from '../../core/module-api';
import type { Point, Shape } from '../../core/scene';
import { cloneShape } from '../../core/scene';
import { transformPath } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"/><path d="M8 8 4 12l4 4"/><path d="M16 8l4 4-4 4"/></svg>';

function reflectShapes(shapes: readonly Shape[], fn: (p: Point) => Point): Shape[] {
  return shapes.map((s) => {
    const c = cloneShape(s);
    c.path = transformPath(s.path, fn);
    return c;
  });
}

export function createMirror(): AffectModule {
  return {
    manifest: {
      id: 'mirror',
      kind: 'affect',
      name: 'Mirror',
      apiVersion: 1,
      description: 'Reflect your marks across the canvas in realtime.',
      icon: ICON,
      params: [
        {
          kind: 'select',
          key: 'axis',
          label: 'Axis',
          default: 'vertical',
          options: [
            { value: 'vertical', label: 'Vertical' },
            { value: 'horizontal', label: 'Horizontal' },
            { value: 'both', label: 'Both (4-way)' },
          ],
        },
      ],
    },

    affect(shapes, ctx) {
      const axis = cfgStr(ctx.config, 'axis', 'vertical');
      const cx = ctx.viewport.width / 2;
      const cy = ctx.viewport.height / 2;
      const rv = (p: Point): Point => ({ x: 2 * cx - p.x, y: p.y });
      const rh = (p: Point): Point => ({ x: p.x, y: 2 * cy - p.y });
      const out: Shape[] = shapes.slice();
      if (axis === 'vertical' || axis === 'both') out.push(...reflectShapes(shapes, rv));
      if (axis === 'horizontal' || axis === 'both') out.push(...reflectShapes(shapes, rh));
      if (axis === 'both') out.push(...reflectShapes(shapes, (p) => rh(rv(p))));
      return out;
    },

    drawGuides(ctx: ModuleContext) {
      const axis = cfgStr(ctx.config, 'axis', 'vertical');
      const cx = ctx.viewport.width / 2;
      const cy = ctx.viewport.height / 2;
      const guideColor = { r: 90, g: 120, b: 255, a: 0.45 };
      if (axis === 'vertical' || axis === 'both') {
        ctx.drawGuide(
          ctx.newShape({
            path: { segments: [{ t: 'M', p: { x: cx, y: 0 } }, { t: 'L', p: { x: cx, y: ctx.viewport.height } }] },
            style: { mode: 'stroke', color: { ...guideColor }, lineWidth: 1, join: 'round', cap: 'butt' },
          }),
        );
      }
      if (axis === 'horizontal' || axis === 'both') {
        ctx.drawGuide(
          ctx.newShape({
            path: { segments: [{ t: 'M', p: { x: 0, y: cy } }, { t: 'L', p: { x: ctx.viewport.width, y: cy } }] },
            style: { mode: 'stroke', color: { ...guideColor }, lineWidth: 1, join: 'round', cap: 'butt' },
          }),
        );
      }
    },
  };
}
